/**
 * @license
 *
 * Copyright 2011 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @fileoverview Map Label.
 *
 * @author Luke Mahe (lukem@google.com),
 *         Chris Broadfoot (cbro@google.com)
 */

var Vector = require('./vector');
var $ = require('jquery');
var d3plus = require('d3plus');

function buildOmbb(polygon, projection, map) {

    var projected = [];

    //get latLng points projected into screen pixels
    for (var z = 0; z < polygon.length - 1; z++) {
        var pixels = projection.fromLatLngToDivPixel(polygon[z]);
        projected.push([pixels.x, pixels.y]);
    }

    //Generate a best fit rectangle using the algorithm here: https://d3plus.org/blog/behind-the-scenes/2014/07/08/largest-rect/
    var rect = d3plus.geom.largestRect(projected)[0];

    var result = {
        width: rect.width,
        height: rect.height,
        angle: rect.angle * -1,
        zoom: map.getZoom(),
        center: projection.fromDivPixelToLatLng(new Vector(rect.cx, rect.cy))
    };

    return result;
}

/**
 * Creates a new Map Label
 * @constructor
 * @extends google.maps.OverlayView
 * @param {Object.<string, *>=} opt_options Optional properties to set.
 */
function MapLabel(opt_options) {
    this.set('fontFamily', 'Montserrat');
    this.set('fontSize', 12);
    this.set('fontColor', '#000000');
    this.set('strokeWeight', 0);
    this.set('strokeColor', '#ffffff');
    this.set('align', 'center');
    this.set('floor', 1);
    this.set('zIndex', 1e3);

    this.setValues(opt_options);

    this.boundingBoxes_ = [];
    this.currentZoom_ = undefined;

    this.gmap_ = opt_options.map;
}

MapLabel.prototype = new google.maps.OverlayView;

window['MapLabel'] = MapLabel;


/** @inheritDoc */
MapLabel.prototype.changed = function(prop) {
    switch (prop) {
    case 'fontFamily':
    case 'fontSize':
    case 'fontColor':
    case 'strokeWeight':
    case 'strokeColor':
    case 'align':
    case 'text':
        return this.drawCanvas_();
    case 'maxZoom':
    case 'minZoom':
    case 'position':
        return this.draw();
    }
};

/**
 * Draws the label to the canvas 2d context.
 * @private
 */
MapLabel.prototype.drawCanvas_ = function() {

    var canvas = this.canvas_;
    var $canvas = this.$canvas_;
    if (!canvas) {
         return;
    }

    //don't draw if not zoomed in to a level with labels
    if (this.getVisible_() === 'hidden') {
        return;
    }

    var ombb = undefined;

    //get current zoom level
    var map = this.getMap();
    var zoom = map.getZoom();

    var projection = this.getProjection();
    var polygon = this.get('polygon');

    if (polygon) {

        //check for cached bounding box
        ombb = this.boundingBoxes_[0];

        //if not build it
        if (!ombb) {
            ombb = this.boundingBoxes_[0] = buildOmbb(polygon, projection, this.gmap_);
        }
    }

    var style = canvas.style;
    style.zIndex = /** @type number */(this.get('zIndex'));


    var minFontSize = 10;
    var maxFontSize = 60;
    var compressor = 1;

    var fontSize = 12; // default

    //Scale width and height based on the current zoom level vs the zoom level they were created at
    var width = ombb.width * (Math.pow(2, zoom - ombb.zoom));
    var height = ombb.height * (Math.pow(2, zoom - ombb.zoom));

    if (ombb) {
        fontSize = Math.max(Math.min(width / (compressor * 10), parseFloat(maxFontSize)), parseFloat(minFontSize));
    }

    //Font size override currently deprecated since the boxes containing the labels are generated via a randomized algorithm
    //and are no longer deterministic
//    var fontSizeOverride = this.get('fontSizeOverride');

//    if (fontSizeOverride && fontSizeOverride > 0) {
//        fontSize = fontSizeOverride;
//    }

    var angle = ombb ? ombb.angle : 0;

    $canvas.outerWidth(width);
    $canvas.outerHeight(height);

    $canvas.css({
        'transform': 'rotate(' + angle + 'deg) translateX(-50%) translateY(-50%)',
        'font-size': fontSize + 'px'
    });

    //Angle override currently deprecated since the boxes containing the labels are generated via a randomized algorithm
    //and are no longer deterministic
//    var angleOverride = this.get('angleOverride');
//    
//    if (angleOverride && angleOverride > 0) {
//        $canvas.find('div')
//            .first()
//            .css({
//                'transform': 'rotate(' + angleOverride + 'deg)'
//            });
//    }
};

/**
 * @inheritDoc
 */
MapLabel.prototype.onAdd = function() {
    var canvas = this.canvas_ = document.createElement('div');
    var inner = document.createElement('div');
    var text = this.get('text');

    var $canvas = this.$canvas_ = $(canvas);
    var $inner = $(inner);

    $inner.addClass('map-label');

    $inner.css({
        'display': 'table-cell',
        'vertical-align': 'middle'
    });

    $inner.html(text.replace(/\\n/g, '<br />')).appendTo($(canvas));

    $canvas.css({
        'position': 'absolute',
//        'border': '1px solid #f00',
        'text-align': 'center',
        'line-height': 1,
        'transform-origin': 'top left',
        'display': 'table',
        'font-family': 'Montserrat',
        'font-weight': '600'
    });

    this.drawCanvas_();

    var panes = this.getPanes();
    if (panes) {
        panes.floatPane.appendChild(canvas);
    }
};
MapLabel.prototype['onAdd'] = MapLabel.prototype.onAdd;


/**
 * @inheritDoc
 */
MapLabel.prototype.draw = function() {

    var projection = this.getProjection();

    if (!projection) {
        // The map projection is not ready yet so do nothing
        return;
    }

    if (!this.canvas_) {
        // onAdd has not been called yet.
        return;
    }

    var latLng = /** @type {google.maps.LatLng} */ (this.get('position'));
    if (!latLng) {
        return;
    }

    var style = this.canvas_.style;
    var visibility = this.getVisible_();

    style['visibility'] = visibility;

    if (visibility === 'hidden') {
        return;
    }

    //recalc ombb
    var ombb;

    var map = this.getMap();
    var zoom = map.getZoom();

    //zoom changed so need to redraw canvas
    if (zoom !== this.currentZoom_) {
        this.currentZoom_ = zoom;

        this.drawCanvas_();
    }

    ombb = this.boundingBoxes_[0];

    var pos = ombb ? projection.fromLatLngToDivPixel(ombb.center) : projection.fromLatLngToDivPixel(latLng);

    style['top'] = pos.y + 'px';
    style['left'] = pos.x + 'px';
};
MapLabel.prototype['draw'] = MapLabel.prototype.draw;

/**
 * Get the visibility of the label.
 * @private
 * @return {string} blank string if visible, 'hidden' if invisible.
 */
MapLabel.prototype.getVisible_ = function() {
    var minZoom = /** @type number */(this.get('minZoom'));
    var maxZoom = /** @type number */(this.get('maxZoom'));

    if (minZoom === undefined && maxZoom === undefined) {
        return '';
    }

    var map = this.getMap();
    if (!map) {
        return '';
    }

    var mapZoom = map.getZoom();
    if (mapZoom < minZoom || mapZoom > maxZoom) {
        return 'hidden';
    }
    return '';
};

/**
 * @inheritDoc
 */
MapLabel.prototype.onRemove = function() {
    var canvas = this.canvas_;
    if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
    }
};
MapLabel.prototype['onRemove'] = MapLabel.prototype.onRemove;